import React from 'react';
import { KBBlock } from '../../../types';

interface CheckItem { text: string; checked: boolean; }
interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

const ChecklistBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const items: CheckItem[] = (block.data?.items ?? [{ text: '', checked: false }]).map((it: any) =>
    typeof it === 'string' ? { text: it, checked: false } : { text: it.text ?? it.content ?? '', checked: !!it.checked }
  );

  const setItems = (next: CheckItem[]) => onChange({ data: { ...block.data, items: next } });

  const toggle = (i: number) => {
    const next = items.map((it, idx) => idx === i ? { ...it, checked: !it.checked } : it);
    setItems(next);
  };

  const updateText = (i: number, text: string) => {
    const next = items.map((it, idx) => idx === i ? { ...it, text } : it);
    setItems(next);
  };

  const addItem = () => setItems([...items, { text: '', checked: false }]);
  const removeItem = (i: number) => setItems(items.filter((_, idx) => idx !== i));

  const handleKeyDown = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'Enter') { e.preventDefault(); addItem(); }
    if (e.key === 'Backspace' && items[i].text === '' && items.length > 1) {
      e.preventDefault();
      removeItem(i);
    }
    if (e.key === 'Escape') onFinish();
  };

  if (isEditing) {
    return (
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-3 group">
            <input
              type="checkbox"
              checked={item.checked}
              onChange={() => toggle(i)}
              className="w-4 h-4 rounded text-blue-600 cursor-pointer"
            />
            <input
              autoFocus={i === items.length - 1}
              type="text"
              value={item.text}
              placeholder="List item"
              onChange={(e) => updateText(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              className={`flex-1 bg-transparent border-none outline-none text-sm ${item.checked ? 'line-through text-gray-400' : 'text-gray-700'}`}
            />
            <button onClick={() => removeItem(i)} className="text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 text-xs">×</button>
          </div>
        ))}
        <button onClick={addItem} className="text-xs text-blue-500 hover:underline mt-1">+ Add item</button>
        <div className="pt-1">
          <button onClick={onFinish} className="text-xs bg-blue-600 text-white px-3 py-1 rounded">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <label key={i} className="flex items-center gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={item.checked}
            onChange={() => {
              const next = items.map((it, idx) => idx === i ? { ...it, checked: !it.checked } : it);
              onChange({ data: { ...block.data, items: next } });
            }}
            className="w-4 h-4 rounded text-blue-600"
          />
          <span className={`text-sm ${item.checked ? 'line-through text-gray-400' : 'text-gray-700'}`}>
            {item.text || <em className="text-gray-300">Empty item</em>}
          </span>
        </label>
      ))}
    </div>
  );
};

export default ChecklistBlock;
